import { db, storage } from '../config/firebase';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { Region } from '../types/region';

const COLLECTION_NAME = 'regions';

// Helper function to check if URL is a Firebase Storage URL
const isFirebaseStorageUrl = (url: string): boolean => {
    try {
        return url.includes('firebasestorage.googleapis.com') || 
               url.includes('storage.googleapis.com') ||
               url.startsWith('gs://');
    } catch {
        return false;
    }
};

export const regionService = {
    async addRegion(region: Region, imageFile?: File) {
        try {
            let imageUrl = '';
            if (imageFile) {
                const storageRef = ref(storage, `region-images/${Date.now()}_${imageFile.name}`);
                const uploadResult = await uploadBytes(storageRef, imageFile);
                imageUrl = await getDownloadURL(uploadResult.ref);
            }

            const docRef = await addDoc(collection(db, COLLECTION_NAME), {
                ...region,
                imageUrl,
                createdAt: new Date(),
                updatedAt: new Date()
            });
            return { id: docRef.id, ...region, imageUrl };
        } catch (error) {
            throw new Error('Không thể thêm vùng trồng: ' + error);
        }
    },

    async updateRegion(id: string, region: Partial<Region>, newImageFile?: File) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const currentDoc = await getDoc(docRef);

            if (!currentDoc.exists()) {
                throw new Error('Không tìm thấy vùng trồng');
            }

            let updateData = {
                ...region,
                updatedAt: new Date()
            };

            if (newImageFile) {
                const oldImageUrl = currentDoc.data().imageUrl;
                if (oldImageUrl && isFirebaseStorageUrl(oldImageUrl)) {
                    try {
                        const oldImageRef = ref(storage, oldImageUrl);
                        await deleteObject(oldImageRef);
                        console.log(`Successfully deleted old region image: ${oldImageUrl}`);
                    } catch (error) {
                        console.error(`Error deleting old region image ${oldImageUrl}:`, error);
                    }
                } else if (oldImageUrl) {
                    console.log(`Skipped non-Firebase Storage URL: ${oldImageUrl}`);
                }

                const storageRef = ref(storage, `region-images/${Date.now()}_${newImageFile.name}`);
                const uploadResult = await uploadBytes(storageRef, newImageFile);
                const newImageUrl = await getDownloadURL(uploadResult.ref);

                updateData = {
                    ...updateData,
                    imageUrl: newImageUrl
                };
            }

            await updateDoc(docRef, updateData);
            return { id, ...updateData };
        } catch (error) {
            throw new Error('Không thể cập nhật vùng trồng: ' + error);
        }
    },

    async deleteRegion(id: string) {
        try {
            const docRef = doc(db, COLLECTION_NAME, id);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                const imageUrl = data.imageUrl;
                
                // Xóa ảnh từ Firebase Storage nếu tồn tại
                if (imageUrl && isFirebaseStorageUrl(imageUrl)) {
                    try {
                        const imageRef = ref(storage, imageUrl);
                        await deleteObject(imageRef);
                        console.log(`Successfully deleted region image: ${imageUrl}`);
                    } catch (error) {
                        console.error(`Error deleting region image ${imageUrl}:`, error);
                        // Tiếp tục xử lý dù có lỗi khi xóa ảnh
                    }
                } else if (imageUrl) {
                    console.log(`Skipped non-Firebase Storage URL: ${imageUrl}`);
                }

                console.log(`Storage cleanup for region ID ${id}: ${imageUrl ? (isFirebaseStorageUrl(imageUrl) ? '1 image deleted' : '1 non-Firebase URL skipped') : 'no image to delete'}`);
            }

            // Xóa document từ Firestore
            await deleteDoc(docRef);
            console.log(`Successfully deleted region document with ID: ${id}`);
        } catch (error) {
            console.error(`Error deleting region with ID ${id}:`, error);
            throw new Error('Không thể xóa vùng trồng: ' + error);
        }
    },

    async getAllRegions() {
        const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as Region[];
    }
}; 